<script setup lang="ts">
// Client-only Add Client User view aligned with company layout, but using client context
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'
import { useUserStore } from '@/stores/userStore'
import { useAccountStore } from '@/stores/accountStore'
import { createUserSimple } from '@/api/accountsAdmin'

const router = useRouter()
const route = useRoute()
const { xs } = useDisplay()
const userStore = useUserStore()
const accountStore = useAccountStore()

// Success dialog
const showSuccess = ref(false)
const snackbar = ref(false)
const message = ref('')
const snackbarColor = ref('error')
const loading = ref(false)

// Form data
const form = ref({ username: '', email: '', role: '' })

// Resolve JUID for payload (prefer query param, fallback to userStore)
const resolvedJuid = computed(() => {
  const fromQuery = String((route.query?.juid as string) || '')
  const fromStore = String((userStore as any)?.juid || '')
  return fromQuery || fromStore
})

// Roles for client context
const roleOptions = [
  { title: 'Client User', value: 'client_user', description: 'Basic client access' },
  { title: 'Client Manager', value: 'client_manager', description: 'Can manage client settings' },
  { title: 'Client Admin', value: 'client_admin', description: 'Full client account access' },
]

// Validation
const rules = {
  required: (v: any) => !!v || 'This field is required',
  email: (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Invalid email address',
  username: (v: string) =>
    /^[a-z0-9_]+$/.test(v) || 'Username must be lowercase letters, numbers, and underscores only',
}
const isFormValid = computed(() => {
  const v = form.value
  return !!(v.username && v.email && v.role)
})

async function handleSubmit() {
  try {
    loading.value = true
    await createUserSimple(
      form.value.username,
      form.value.email,
      form.value.role,
      resolvedJuid.value
    )
    // Invalidate caches so Users list refreshes
    try {
      accountStore.setClientUsersCache([])
      accountStore.setClientAccountsUsersCache([])
    } catch {}
    showSuccess.value = true
  } catch (error: any) {
    let errorMsg = 'Failed to create user. Please try again.'
    if (error?.response?.data?.message) errorMsg = error.response.data.message
    else if (error?.response?.data)
      errorMsg =
        typeof error.response.data === 'string'
          ? error.response.data
          : JSON.stringify(error.response.data)
    else if (error?.message) errorMsg = error.message
    message.value = errorMsg
    snackbarColor.value = 'error'
    snackbar.value = true
  } finally {
    loading.value = false
  }
}

function handleSuccessOk() {
  showSuccess.value = false
  router.push('/users')
}

function goBack() {
  router.back()
}
</script>

<template>
  <v-container fluid class="pa-6">
    <v-row>
      <v-col cols="12">
        <!-- Header with Back Button -->
        <div class="d-flex align-center mb-6" style="gap: 8px">
          <v-btn
            variant="text"
            size="small"
            prepend-icon="mdi-arrow-left"
            class="text-grey-darken-2"
            @click="goBack"
          >
            Back
          </v-btn>
          <h2 class="text-h5 font-weight-medium" style="margin: 0">Add Client User</h2>
        </div>

        <!-- Success Dialog -->
        <v-dialog v-model="showSuccess" max-width="500" persistent data-testid="success-dialog">
          <v-card>
            <v-card-title class="text-h6 bg-primary text-white pa-4">
              <v-icon icon="mdi-check-circle" class="mr-2"></v-icon>
              User Created Successfully!
            </v-card-title>
            <v-card-text class="pa-6">
              <p class="mb-2">An invitation email with a temporary password has been sent to:</p>
              <p class="font-weight-bold mb-0" data-testid="success-email">{{ form.email }}</p>
            </v-card-text>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn
                data-testid="success-ok-btn"
                color="primary"
                variant="flat"
                @click="handleSuccessOk"
              >
                OK
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Form Card -->
        <v-card class="mx-auto" max-width="800">
          <v-card-text class="pa-6">
            <v-form @submit.prevent="handleSubmit">
              <!-- Account is implicit (client context); no account selection shown -->

              <v-text-field
                data-testid="username-input"
                v-model="form.username"
                label="Username *"
                placeholder="Enter username"
                :rules="[rules.required, rules.username]"
                prepend-inner-icon="mdi-account"
                variant="outlined"
                density="comfortable"
                class="mb-4"
              />

              <v-text-field
                data-testid="email-input"
                v-model="form.email"
                label="Email *"
                placeholder="Enter email address"
                type="email"
                :rules="[rules.required, rules.email]"
                prepend-inner-icon="mdi-email"
                variant="outlined"
                density="comfortable"
                class="mb-4"
              />

              <v-select
                data-testid="role-select"
                v-model="form.role"
                :items="roleOptions"
                item-title="title"
                item-value="value"
                label="Role *"
                placeholder="Select a role"
                :rules="[rules.required]"
                prepend-inner-icon="mdi-shield-account"
                variant="outlined"
                density="comfortable"
                class="mb-6"
              />

              <!-- Info Alert -->
              <v-alert type="info" variant="tonal" density="compact" class="mb-6">
                <div class="text-body-2">
                  <strong>What happens next:</strong>
                  <ul class="mt-2 ml-4">
                    <li>User will receive an invitation email with a temporary password</li>
                    <li>They'll be prompted to set a new password on first login</li>
                    <li>Email is automatically verified</li>
                  </ul>
                </div>
              </v-alert>

              <!-- Action Buttons -->
              <div class="d-flex justify-end" style="gap: 12px">
                <v-btn data-testid="cancel-btn" variant="text" @click="goBack" :disabled="loading">
                  Cancel
                </v-btn>
                <v-btn
                  data-testid="create-user-btn"
                  type="submit"
                  color="primary"
                  variant="flat"
                  :loading="loading"
                  :disabled="!isFormValid || loading"
                  prepend-icon="mdi-account-plus"
                >
                  Create User
                </v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="5000">
      {{ message }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<style scoped>
::deep(.v-field) {
  border-radius: 8px;
}
::deep(.v-alert) {
  border-radius: 8px;
}
</style>
